import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as XLSX from 'xlsx';

import { reconcileBulkPayments, commitBulkPayments } from '../../api/Payments';
import { Loading, PageHeader } from '../../components';
import useComponentState from '../../utils/useComponentState';
import styles from './BulkAddPayment.module.scss';

const BulkAddPayment = ({ auth }) => {
  const [state, setState] = useComponentState({
    uploadedFile: null,
    loading: false,
    reconcileResponse: null,
    checkedGroups: [],
    notifyMerchantViaEmail: true,
    committing: false,
    commitResponse: null,
    error: null,
  });

  const {
    uploadedFile,
    loading,
    reconcileResponse,
    checkedGroups,
    notifyMerchantViaEmail,
    committing,
    commitResponse,
    error,
  } = state;

  // A Payment ID alone no longer uniquely identifies a group — the same reference code
  // can appear twice (once per distinct payment date), so selection/keys must use both.
  const getGroupKey = (group) => `${group.referenceCode}__${group.paymentDate}`;

  // sheet and row validation error msgs
  const KNOWN_ERROR_MESSAGES = {
    EMPTY_EXCEL_FILE: 'This file appears to be empty. Please upload a completed payment sheet.',
    MISSING_EXCEL_HEADERS:
      'This file is missing one or more required columns. Please use the correct payment sheet template.',
    UNREADABLE_EXCEL_FILE: "This file couldn't be read. Please make sure it's a valid .xlsx file.",
    UNRECOGNISED_REDEMPTION_DATE_FORMAT:
      "One of the Redemption Date values couldn't be read. Please check the date format in that column.",
  };

  const getErrorMessage = (err, fallback) => {
    const code = err?.detail || err?.message;
    if (!code) {
      return fallback;
    }
    if (code.startsWith('MISSING_EXCEL_HEADERS')) {
      const missingHeaders = code.split(':')[1]?.trim();
      return missingHeaders
        ? `Required column(s) '${missingHeaders}' are missing from the uploaded file. Please use the correct payment sheet template.`
        : KNOWN_ERROR_MESSAGES.MISSING_EXCEL_HEADERS;
    }
    return KNOWN_ERROR_MESSAGES[code] || code || fallback;
  };

  // reads workbook n check if details are missing n thriw error msgs
  const readWorkbookRows = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const workbook = XLSX.read(event.target.result, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = sheet
            ? XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false })
            : [];
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileChange = (event) => {
    setState({ uploadedFile: event.target.files[0], error: null });
  };

  const handleReconcile = async () => {
    if (!uploadedFile) {
      setState({ error: 'Please select an Excel file to upload' });
      return;
    }

    setState({
      loading: true,
      error: null,
      reconcileResponse: null,
      checkedGroups: [],
      commitResponse: null,
    });

    try {
      const rows = await readWorkbookRows(uploadedFile);
      if (rows.length === 0) {
        const message = KNOWN_ERROR_MESSAGES.EMPTY_EXCEL_FILE;
        setState({ loading: false, error: message });
        toast.error(message);
        return;
      }
    } catch (err) {
      const message = KNOWN_ERROR_MESSAGES.UNREADABLE_EXCEL_FILE;
      setState({ loading: false, error: message });
      toast.error(message);
      return;
    }

    try {
      const response = await reconcileBulkPayments(uploadedFile, auth.token);
      setState({
        loading: false,
        reconcileResponse: response,
        checkedGroups: response.matchedGroups.map((group) => getGroupKey(group)),
      });
      toast.success(
        `Reconciled ${response.totalRows} rows: ${response.matchedRowCount} matched, ${response.unmatchedRowCount} need review`
      );
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to reconcile file. Please try again.');
      setState({ loading: false, error: message });
      toast.error(message);
    }
  };

  const handleGroupCheckToggle = (groupKey) => {
    const isChecked = checkedGroups.includes(groupKey);
    setState({
      checkedGroups: isChecked
        ? checkedGroups.filter((key) => key !== groupKey)
        : [...checkedGroups, groupKey],
    });
  };

  const handleCommit = async () => {
    const groupsToCommit = reconcileResponse.matchedGroups
      .filter((group) => checkedGroups.includes(getGroupKey(group)))
      .map((group) => ({
        referenceCode: group.referenceCode,
        merchantId: group.merchantId,
        redemptionIds: group.redemptionIds,
        paymentDate: group.paymentDate,
        // TEMP-NET-PAYABLE
        totalPayable: group.totalPayable,
      }));

    if (groupsToCommit.length === 0) {
      toast.error('Select at least one group to process');
      return;
    }

    setState({ committing: true, commitResponse: null });

    try {
      const response = await commitBulkPayments(
        { groups: groupsToCommit, notifyMerchantViaEmail },
        auth.token
      );
      setState({ committing: false, commitResponse: response });
      if (response.failureCount === 0) {
        toast.success(`All ${response.successCount} payment(s) created successfully`);
      } else {
        toast.warning(
          `${response.successCount} succeeded, ${response.failureCount} failed — see results below`
        );
      }
    } catch (err) {
      setState({ committing: false });
      toast.error(getErrorMessage(err, 'Failed to process payments'));
    }
  };

  const renderCommitResults = () => {
    if (!commitResponse) {
      return null;
    }
    return (
      <section className={styles.section}>
        <div className={styles.section_header}>
          <h5 className={styles.section_title}>Payment Results</h5>
          <span className={`${styles.badge} ${styles.badge_success}`}>
            {commitResponse.successCount} succeeded
          </span>
          {commitResponse.failureCount > 0 && (
            <span className={`${styles.badge} ${styles.badge_danger}`}>
              {commitResponse.failureCount} failed
            </span>
          )}
        </div>
        <Table bordered hover size='sm' responsive className={styles.table}>
          <thead>
            <tr>
              <th>Reference Code</th>
              <th>Status</th>
              <th>Payment ID</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {commitResponse.results.map((result, index) => (
              <tr key={`${result.referenceCode}-${index}`}>
                <td>{result.referenceCode}</td>
                <td>
                  <span
                    className={`${styles.badge} ${
                      result.status === 'SUCCESS' ? styles.badge_success : styles.badge_danger
                    }`}
                  >
                    {result.status}
                  </span>
                </td>
                <td>{result.paymentId || '-'}</td>
                <td>{result.message}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {commitResponse.failureCount > 0 && (
          <small className={styles.hint}>
            Some payments failed. Upload the sheet again to retry them — payments that were already
            created will show as already paid.
          </small>
        )}
      </section>
    );
  };

  const renderMatchedGroupsTable = () => {
    const { matchedGroups } = reconcileResponse;
    if (matchedGroups.length === 0) {
      return <p className={styles.empty}>No matched groups ready to pay.</p>;
    }
    const hasAnyWarnings = matchedGroups.some(
      (group) => group.warnings && group.warnings.length > 0
    );
    return (
      <Table bordered hover size='sm' responsive className={styles.table}>
        <thead>
          <tr>
            <th></th>
            <th>Merchant</th>
            <th>Reference Code</th>
            <th>Payment Date</th>
            <th className={styles.num}># Redemptions</th>
            <th className={styles.num}>Total Payable (LKR)</th>
            {hasAnyWarnings && <th>Warnings</th>}
          </tr>
        </thead>
        <tbody>
          {matchedGroups.map((group) => (
            <tr key={getGroupKey(group)}>
              <td>
                <input
                  type='checkbox'
                  checked={checkedGroups.includes(getGroupKey(group))}
                  disabled={Boolean(commitResponse)}
                  onChange={() => handleGroupCheckToggle(getGroupKey(group))}
                />
              </td>
              <td>{group.merchantName}</td>
              <td>{group.referenceCode}</td>
              <td>{moment(group.paymentDate).format('DD/MM/YYYY')}</td>
              <td className={styles.num}>{group.redemptionIds.length}</td>
              <td className={styles.num}>
                {group.totalPayable.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              </td>
              {hasAnyWarnings && (
                <td>
                  {group.warnings && group.warnings.length > 0 && (
                    <small className='text-danger'>{group.warnings.join('; ')}</small>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const renderUnmatchedRowsTable = () => {
    const { unmatchedRows } = reconcileResponse;
    if (unmatchedRows.length === 0) {
      return <p className={styles.empty}>No rows need review.</p>;
    }
    return (
      <Table bordered hover size='sm' responsive className={styles.table}>
        <thead>
          <tr>
            <th>Row #</th>
            <th>Redemption ID</th>
            <th>Merchant</th>
            <th>Sub Merchant ID</th>
            <th className={styles.num}>Total (LKR)</th>
            <th>Redemption Date</th>
            <th>Reason</th>
            <th>Message</th>
          </tr>
        </thead>
        <tbody>
          {unmatchedRows.map((row) => (
            <tr key={row.rowNumber}>
              <td>{row.rowNumber}</td>
              <td>{row.redemptionId}</td>
              <td>{row.merchantName}</td>
              <td>{row.subMerchantId}</td>
              <td className={styles.num}>{row.total?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</td>
              <td>{row.redemptionDate ? moment(row.redemptionDate).format('DD/MM/YYYY HH:mm') : ''}</td>
              <td>{row.reasonCode}</td>
              <td>{row.message}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <div className={styles.root}>
      <ToastContainer />
      <PageHeader text='Bulk Add Payment' />

      <section className={`${styles.section} ${styles.upload_card}`}>
        <div className={styles.upload_text}>
          <h5 className={styles.section_title}>Upload payment sheet</h5>
          <p className={styles.hint}>
            Choose the completed .xlsx payment sheet, then reconcile it against redemptions.
          </p>
        </div>
        <Form.Group controlId='paymentSheetUpload' className={styles.upload_row}>
          <Form.Label className='visually-hidden'>Upload Payment Sheet (.xlsx)</Form.Label>
          <Form.Control
            type='file'
            accept='.xlsx'
            onChange={handleFileChange}
            className={styles.file_input}
          />
          <Button
            variant='dark'
            className={styles.primary_btn}
            onClick={handleReconcile}
            disabled={loading}
          >
            {loading ? 'Reconciling...' : 'Upload & Reconcile'}
          </Button>
        </Form.Group>
        {error && <small className={styles.error}>{error}</small>}
      </section>

      {loading && <Loading text='Reconciling' />}

      {reconcileResponse && (
        <>
          <div className={styles.stats}>
            <div className={styles.stat}>
              <span className={styles.stat_label}>Total rows</span>
              <span className={styles.stat_value}>{reconcileResponse.totalRows}</span>
            </div>
            <div className={`${styles.stat} ${styles.stat_success}`}>
              <span className={styles.stat_label}>Matched</span>
              <span className={styles.stat_value}>{reconcileResponse.matchedRowCount}</span>
            </div>
            <div className={`${styles.stat} ${styles.stat_warning}`}>
              <span className={styles.stat_label}>Needs review</span>
              <span className={styles.stat_value}>{reconcileResponse.unmatchedRowCount}</span>
            </div>
          </div>

          <section className={styles.section}>
            <div className={styles.section_header}>
              <h5 className={styles.section_title}>Ready to Pay</h5>
              <span className={styles.badge}>
                {reconcileResponse.matchedGroups.length} groups
              </span>
            </div>
            {renderMatchedGroupsTable()}

            {reconcileResponse.matchedGroups.length > 0 && !commitResponse && (
              <div className={styles.commit_bar}>
                <Form.Check
                  type='checkbox'
                  id='notifyMerchantViaEmail'
                  label='Send email notification to merchant'
                  checked={notifyMerchantViaEmail}
                  onChange={(e) => setState({ notifyMerchantViaEmail: e.target.checked })}
                />
                <Button
                  variant='dark'
                  className={styles.primary_btn}
                  onClick={handleCommit}
                  disabled={committing || checkedGroups.length === 0}
                >
                  {committing
                    ? 'Processing...'
                    : `Confirm & Create ${checkedGroups.length} Payment(s)`}
                </Button>
              </div>
            )}
          </section>

          {renderCommitResults()}

          <section className={styles.section}>
            <div className={styles.section_header}>
              <h5 className={styles.section_title}>Needs Review</h5>
              <span className={`${styles.badge} ${styles.badge_warning}`}>
                {reconcileResponse.unmatchedRows.length} rows
              </span>
            </div>
            {renderUnmatchedRowsTable()}
          </section>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth.auth,
  };
};

export default connect(mapStateToProps)(BulkAddPayment);
